import type { IconName } from '@fortawesome/fontawesome-common-types';

import {
  anxiety,
  arterialStiffness,
  bmi,
  bodyFat,
  bp,
  cardioFitness,
  cholesterol,
  crpi,
  depression,
  framinghamScore,
  hba1c,
  hdl,
  lap,
  ldl,
  metsDegree,
  rhr,
  tenYrCvd,
  triglycerides,
  vai,
  waistHeight,
  waistHip,
} from '@constants/reference-range/index';

import {
  type ArterialStiffnessRiskLevel,
  BloodPressureRiskLevel,
  BmiRiskLevel,
  BodyFatRiskLevel,
  RiskLevel,
} from '@models/assessment';

import {
  ANXIETY,
  ARTERIAL_STIFFNESS,
  BLOOD_PRESSURE,
  BODY_FAT_PERCENTAGE,
  BODY_MASS_INDEX,
  C_REACTIVE_PROTEIN_I,
  CARDIORESPIRATORY_FITNESS,
  CHOLESTEROL,
  DEPRESSION,
  FRAMINGHAM_SCORE,
  HBA1C,
  HDL_CHOL,
  LDL_CHOL,
  LIPID_ACCUMULATION_PRODUCT,
  MET_S_DEGREE,
  RESTING_HEART_RATE,
  TEN_YEAR_CVD_RISK,
  TRIGLYCERIDES,
  VISCERAL_ADIPOSITY_INDEX,
  WAIST_HEIGHT_RATIO,
  WAIST_HIP_RATIO,
} from '../fhir';
import type { ReferenceRange } from '../reference-range/types';

interface AssessmentSectionData<TRiskLevel extends string> {
  title?: string;
  description: string;
  referenceRange: ReferenceRange<TRiskLevel>;
  /* Display 'Applicable to your age and biological sex.' text?  */
  applicable?: boolean;
  /* Text in range section */
  subtext?: string[];
  /* Include metric units in displayed ref ranges */
  includeUnits?: boolean;
}

const bpAssessmentData: AssessmentSectionData<BloodPressureRiskLevel> = {
  title: 'vitalSigns.bloodPressure',
  description: 'bhaResults.bloodPressure.description',
  subtext: ['Systolic', '', 'Diastolic'],
  referenceRange: bp,
};
const rhrAssessmentData: AssessmentSectionData<RiskLevel> = {
  title: 'vitalSigns.restingHeartRate',
  description: 'bhaResults.restingHr.description',
  applicable: true,
  referenceRange: rhr,
};
const cardioFitnessAssessmentData: AssessmentSectionData<RiskLevel> = {
  title: 'bhaResults.cardioFitness.title',
  description: 'bhaResults.cardioFitness.description',
  applicable: true,
  subtext: ['ml/kg/min'],
  referenceRange: cardioFitness,
};
const arterialStiffnessAssessmentData: AssessmentSectionData<ArterialStiffnessRiskLevel> =
  {
    title: 'bhaResults.arterialStiffness.title',
    description: 'bhaResults.arterialStiffness.description',
    subtext: ['cm/s'],
    referenceRange: arterialStiffness,
  };
const tenYrCvdAssessmentData: AssessmentSectionData<RiskLevel> = {
  title: 'bhaResults.tenYrCvdRisk.title',
  description: 'bhaResults.tenYrCvdRisk.description',
  includeUnits: true,
  referenceRange: tenYrCvd,
};
const framinghamScoreAssessmentData: AssessmentSectionData<RiskLevel> = {
  title: 'bhaResults.framinghamScore.title',
  description: 'bhaResults.framinghamScore.description',
  includeUnits: true,
  referenceRange: framinghamScore,
};
const bmiAssessmentData: AssessmentSectionData<BmiRiskLevel> = {
  title: 'bhaResults.bmi.title',
  description: 'bhaResults.bmi.description',
  subtext: ['kg/m2'],
  referenceRange: bmi,
};
const waistHeightAssessmentData: AssessmentSectionData<RiskLevel> = {
  title: 'bhaResults.waistHeight.title',
  description: 'bhaResults.waistHeight.description',
  referenceRange: waistHeight,
};
const waistHipAssessmentData: AssessmentSectionData<RiskLevel> = {
  title: 'bhaResults.waistHip.title',
  description: 'bhaResults.waistHip.description',
  applicable: true,
  referenceRange: waistHip,
};
const bodyFatAssessmentData: AssessmentSectionData<BodyFatRiskLevel> = {
  title: 'bhaResults.bodyFat.title',
  description: 'bhaResults.bodyFat.description',
  applicable: true,
  subtext: ['Body fat %'],
  includeUnits: true,
  referenceRange: bodyFat,
};
const vaiAssessmentData: AssessmentSectionData<RiskLevel> = {
  title: 'bhaResults.vai.title',
  description: 'bhaResults.vai.description',
  referenceRange: vai,
};
const hba1cAssessmentData: AssessmentSectionData<RiskLevel> = {
  title: 'bhaResults.hba1c.title',
  description: 'bhaResults.hba1c.description',
  includeUnits: true,
  referenceRange: hba1c,
};
const cholesterolAssessmentData: AssessmentSectionData<RiskLevel> = {
  title: 'bhaResults.cholesterol.title',
  description: 'bhaResults.cholesterol.description',
  subtext: ['mmol/L'],
  referenceRange: cholesterol,
};
const hdlAssessmentData: AssessmentSectionData<RiskLevel> = {
  title: 'bhaResults.hdl.title',
  description: 'bhaResults.hdl.description',
  subtext: ['mmol/L'],
  referenceRange: hdl,
};
const ldlAssessmentData: AssessmentSectionData<RiskLevel> = {
  title: 'bhaResults.ldl.title',
  description: 'bhaResults.ldl.description',
  subtext: ['mmol/L'],
  referenceRange: ldl,
};
const triglycerideAssessmentData: AssessmentSectionData<RiskLevel> = {
  title: 'bhaResults.triglycerides.title',
  description: 'bhaResults.triglycerides.description',
  subtext: ['mmol/L'],
  referenceRange: triglycerides,
};
const lapAssessmentData: AssessmentSectionData<RiskLevel> = {
  title: 'bhaResults.lap.title',
  description: 'bhaResults.lap.description',
  referenceRange: lap,
};
const crpiAssessmentData: AssessmentSectionData<RiskLevel> = {
  title: 'bhaResults.crpi.title',
  description: 'bhaResults.crpi.description',
  includeUnits: true,
  referenceRange: crpi,
};
const metsDegreeAssessmentData: AssessmentSectionData<RiskLevel> = {
  title: 'bhaResults.metsDegree.title',
  description: 'bhaResults.metsDegree.description',
  referenceRange: metsDegree,
};
const depressionAssessmentData: AssessmentSectionData<RiskLevel> = {
  title: 'bhaResults.despression.title',
  description: 'bhaResults.despression.description',
  referenceRange: depression,
};
const anxietyAssessmentData: AssessmentSectionData<RiskLevel> = {
  title: 'bhaResults.anxiety.title',
  description: 'bhaResults.anxiety.description',
  referenceRange: anxiety,
};

export const ASSESSMENT_RESULT_MAP = new Map<
  string,
  AssessmentSectionData<string>
>([
  [BLOOD_PRESSURE, bpAssessmentData],
  [RESTING_HEART_RATE, rhrAssessmentData],
  [CARDIORESPIRATORY_FITNESS, cardioFitnessAssessmentData],
  [ARTERIAL_STIFFNESS, arterialStiffnessAssessmentData],
  [TEN_YEAR_CVD_RISK, tenYrCvdAssessmentData],
  [FRAMINGHAM_SCORE, framinghamScoreAssessmentData],
  [BODY_MASS_INDEX, bmiAssessmentData],
  [WAIST_HEIGHT_RATIO, waistHeightAssessmentData],
  [WAIST_HIP_RATIO, waistHipAssessmentData],
  [BODY_FAT_PERCENTAGE, bodyFatAssessmentData],
  [VISCERAL_ADIPOSITY_INDEX, vaiAssessmentData],
  [HBA1C, hba1cAssessmentData],
  [CHOLESTEROL, cholesterolAssessmentData],
  [HDL_CHOL, hdlAssessmentData],
  [LDL_CHOL, ldlAssessmentData],
  [TRIGLYCERIDES, triglycerideAssessmentData],
  [LIPID_ACCUMULATION_PRODUCT, lapAssessmentData],
  [C_REACTIVE_PROTEIN_I, crpiAssessmentData],
  [MET_S_DEGREE, metsDegreeAssessmentData],
  [DEPRESSION, depressionAssessmentData],
  [ANXIETY, anxietyAssessmentData],
]);

export const ASSESSMENT_SECTIONS_MAP: Record<
  string,
  { icon: IconName; metrics: string[] }
> = {
  'bhaResults.section.cardiovascular': {
    icon: 'heart-circle-plus',
    metrics: [
      BLOOD_PRESSURE,
      RESTING_HEART_RATE,
      CARDIORESPIRATORY_FITNESS,
      ARTERIAL_STIFFNESS,
      TEN_YEAR_CVD_RISK,
      FRAMINGHAM_SCORE,
    ],
  },

  'bhaResults.section.bodyComposition': {
    icon: 'person',
    metrics: [
      BODY_MASS_INDEX,
      WAIST_HEIGHT_RATIO,
      WAIST_HIP_RATIO,
      BODY_FAT_PERCENTAGE,
      VISCERAL_ADIPOSITY_INDEX,
    ],
  },
  'bhaResults.section.diabetes': { icon: 'droplet', metrics: [HBA1C] },
  'bhaResults.section.lipid': {
    icon: 'circle',
    metrics: [
      CHOLESTEROL,
      HDL_CHOL,
      LDL_CHOL,
      TRIGLYCERIDES,
      LIPID_ACCUMULATION_PRODUCT,
    ],
  },
  'bhaResults.section.inflammation': {
    icon: 'shield-virus',
    metrics: [C_REACTIVE_PROTEIN_I],
  },
  'bhaResults.section.metabolic': { icon: 'heart', metrics: [MET_S_DEGREE] },
  'bhaResults.section.depressionAnxiety': {
    icon: 'brain',
    metrics: [DEPRESSION, ANXIETY],
  },
};

export const ASSESSMENT_RISK_MAP: Record<
  string,
  { color: string; label: string; condition?: string }
> = {
  [RiskLevel.Low]: {
    color: 'success.main',
    label: 'bhaResults.lowRisk',
  },
  [RiskLevel.Medium]: {
    color: 'warning.dark',
    label: 'bhaResults.mediumRisk',
  },
  [RiskLevel.High]: {
    color: 'error.main',
    label: 'bhaResults.highRisk',
  },
  // BMI
  [BmiRiskLevel.High1]: {
    color: 'error.main',
    label: 'bhaResults.highRisk',
  },
  [BmiRiskLevel.High2]: {
    color: 'error.main',
    label: 'bhaResults.highRisk',
  },
  // Body fat %
  [BodyFatRiskLevel.Medium1]: {
    color: 'warning.dark',
    label: 'bhaResults.mediumRisk',
  },
  [BodyFatRiskLevel.Medium2]: {
    color: 'warning.dark',
    label: 'bhaResults.mediumRisk',
  },
  // BP
  [BloodPressureRiskLevel.Hypotension]: {
    color: 'warning.dark',
    label: 'bhaResults.hypotension',
    condition: 'bhaResults.or',
  },
  [BloodPressureRiskLevel.Normal]: {
    color: 'success.main',
    label: 'bhaResults.normal',
    condition: 'bhaResults.and',
  },
  [BloodPressureRiskLevel.Elevated]: {
    color: 'warning.dark',
    label: 'bhaResults.elevated',
    condition: 'bhaResults.and',
  },
  [BloodPressureRiskLevel.Hypertension1]: {
    color: 'warning.dark',
    label: 'bhaResults.hypertension1',
    condition: 'bhaResults.or',
  },
  [BloodPressureRiskLevel.Hypertension2]: {
    color: 'error.main',
    label: 'bhaResults.hypertension2',
    condition: 'bhaResults.or',
  },
  [BloodPressureRiskLevel.HypertensionCrisis]: {
    color: 'error.main',
    label: 'bhaResults.hypertensionCrisis',
    condition: 'bhaResults.or',
  },
};
